// src/hooks/useCreatorRecap.js
//
// Powers the "While you were gone" card on Home — but ONLY for
// creators, and only using real, verifiable numbers. This is the
// honest version of a command-center dashboard: a bounded, calm
// recap, not a vanity-metrics wall, with a clear path to the full
// Dashboard for anyone who wants more depth.
//
// Takes `previousVisitAt` from useLastVisit rather than tracking a
// visit timestamp itself — useLastVisit is the single source of
// truth for "when were they last here," shared with the fan-facing
// "new since you were last here" marker. This hook only owns the
// creator-specific piece: the view-count snapshot needed to compute
// a views delta.
//
// SCHEMA — confirmed against the real codebase:
//   - drops docs have `creatorId`, `viewCount`, `maxViews`, `status`
//     ("open" | "closed") — already used elsewhere in Home.jsx.
//   - follows docs are `follows/{followerId}_{creatorId}` with
//     `followerId`, `creatorId`, and `createdAt: serverTimestamp()`,
//     written by `followService.toggleFollow` (and referral
//     auto-follow, same shape). Confirmed real, not assumed.
//   - SEED-DATA CAVEAT: nothing in the seed scripts writes to the
//     `follows` collection — seed following relationships only live
//     on `users.following` arrays. That means newFollowers will read
//     0 for any creator whose followers came from seed data, even
//     though the count is fully accurate for real follows created
//     through the actual UI. Not a bug — just don't be confused when
//     testing against seed data and this stat stays at zero.
//   - There is no existing "drop closed at" timestamp being read
//     here; newlyLocked counts drops that are closed AND whose
//     viewCount is at/above maxViews, which is a reasonable proxy but
//     can't tell you exactly *when* it closed relative to
//     previousVisitAt the way a true `closedAt` field would. Fine
//     for v1.

import { useEffect, useState } from "react";
import { collection, doc, getDoc, getDocs, query, setDoc, where } from "firebase/firestore";
import { db } from "../firebase";

const MIN_AWAY_MINUTES = 20; // below this, "while you were gone" isn't meaningful — skip the card

function toMillis(value) {
  if (!value) return 0;
  if (typeof value.toDate === "function") return value.toDate().getTime();
  if (typeof value.seconds === "number") return value.seconds * 1000;
  return 0;
}

export default function useCreatorRecap(currentUser, isCreator, previousVisitAt) {
  const [recap, setRecap] = useState(null); // null = no card to show
  const [loading, setLoading] = useState(Boolean(isCreator));

  useEffect(() => {
    if (!currentUser || !isCreator) {
      setRecap(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;

    async function run() {
      setLoading(true);
      try {
        const previousVisitMillis = toMillis(previousVisitAt);
        const nowMillis = Date.now();

        // First-ever visit — nothing to compare against, skip entirely.
        if (!previousVisitMillis) {
          if (!cancelled) setRecap(null);
          setLoading(false);
          return;
        }

        const minutesAway = Math.round((nowMillis - previousVisitMillis) / 60000);
        if (minutesAway < MIN_AWAY_MINUTES) {
          if (!cancelled) setRecap(null);
          setLoading(false);
          return;
        }

        const userRef = doc(db, "users", currentUser.uid);
        const userSnap = await getDoc(userRef);
        const userData = userSnap.exists() ? userSnap.data() : {};
        const lastViewTotal = typeof userData.lastHomeViewTotal === "number" ? userData.lastHomeViewTotal : 0;

        // ── This creator's own drops ──
        const dropsSnap = await getDocs(
          query(collection(db, "drops"), where("creatorId", "==", currentUser.uid))
        );
        const drops = dropsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

        const currentViewTotal = drops.reduce((sum, d) => sum + (d.viewCount || 0), 0);
        const newViews = Math.max(currentViewTotal - lastViewTotal, 0);

        // "New paid unlocks" — no new schema needed: paid/subscription
        // drops accumulate views the same way free ones do (a view IS
        // an unlock on a paid drop), so this reuses the same
        // view-count-delta math, just filtered to accessType. A real,
        // honest count — not a dollar figure. Actual revenue needs
        // price-at-purchase and fee math that belongs in the real
        // Earnings dashboard, not a quick daily glance.
        const paidDrops = drops.filter((d) => d.accessType === "paid" || d.accessType === "subscription");
        const lastPaidViewTotal = typeof userData.lastHomePaidViewTotal === "number" ? userData.lastHomePaidViewTotal : 0;
        const currentPaidViewTotal = paidDrops.reduce((sum, d) => sum + (d.viewCount || 0), 0);
        const newPaidUnlocks = Math.max(currentPaidViewTotal - lastPaidViewTotal, 0);

        const newlyLocked = drops.filter(
          (d) => d.status === "closed" && (d.viewCount || 0) >= (d.maxViews || 100)
        ).length;

        // ── New followers since previous visit ──
        // Field confirmed real (see file header) — but will read 0
        // against seed-only follow relationships, which don't have a
        // matching follows doc. Accurate for real follows.
        const followsSnap = await getDocs(
          query(collection(db, "follows"), where("creatorId", "==", currentUser.uid))
        );
        const newFollowers = followsSnap.docs.filter(
          (f) => toMillis(f.data().createdAt) > previousVisitMillis
        ).length;

        // ── Write the view-total snapshots for next time ──
        if (!cancelled) {
          setDoc(
            userRef,
            { lastHomeViewTotal: currentViewTotal, lastHomePaidViewTotal: currentPaidViewTotal },
            { merge: true }
          ).catch((err) => console.error("useCreatorRecap snapshot write error:", err));
        }

        const hoursAway = minutesAway >= 60 ? Math.round(minutesAway / 60) : null;

        if (!cancelled) {
          setRecap({ minutesAway, hoursAway, newViews, newPaidUnlocks, newlyLocked, newFollowers });
        }
      } catch (err) {
        console.error("useCreatorRecap error:", err);
        if (!cancelled) setRecap(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();
    return () => { cancelled = true; };
  }, [currentUser, isCreator, previousVisitAt]);

  return { recap, loading };
}
